import type { User } from "firebase/auth";
import type { OpportunityDecisionRoom } from "@tradvera/contracts";
import type { HermesFinancialReportV1 } from "@tradvera/contracts";

export type CompanionNotification = {
  notificationId: string;
  eventId: string;
  threadId: string;
  category: string;
  severity: "info" | "watch" | "warning" | "urgent";
  deliveryPolicy: string;
  titleAr: string;
  titleEn: string;
  bodyAr: string;
  bodyEn: string;
  sourceLabel: string;
  sourceHealth: string;
  evidence: {
    evidenceAr?: string;
    evidenceEn?: string;
    entityIds?: string[];
    materialityScore?: number;
    timeSensitivityMinutes?: number;
  };
  reasonCodes: string[];
  deepLink: string;
  narrationMode: string;
  knownAt: string;
  expiresAt: string;
  createdAt: string;
  latestState: string;
  latestStateAt: string;
  opened: boolean;
  acknowledged: boolean;
};

export type CompanionPortfolio = {
  sourceState: "connected" | "unbound";
  book: "paper";
  capitalNature: "virtual";
  realMoney: false;
  liveExecution: false;
  currentEquity: number | null;
  availableCash: number | null;
  currency: string;
  realizedPnl: number | null;
  unrealizedPnl: number | null;
  asOf: string | null;
  positionsCount: number;
  currentOrdersCount: number;
  policyState: "active" | "draft" | "superseded" | "unbound";
  planVersion: number | null;
  sleeves: Array<{
    id: "intraday_speculator" | "swing_trader" | "long_term_investor";
    labelAr: string;
    labelEn: string;
    enabled: boolean;
    capitalPct: number;
    capitalAmount: number | null;
    cadence: "real_time" | "eod" | "weekly";
    horizons: string[];
  }>;
};

export type CompanionBootstrap = {
  service: string;
  mode: string;
  generatedAt: string;
  notifications: CompanionNotification[];
  unreadCount: number;
  push: {
    configured: boolean;
    publicKey: string | null;
    activeSubscriptions: number;
  };
  hermes: {
    proactive: boolean;
    deterministicPriority: boolean;
    marketExecutionAllowed: boolean;
    fallbackNarration: string;
  };
  portfolio: CompanionPortfolio;
  financialReport: HermesFinancialReportV1;
};

export type HermesChatMessage = { role: "user" | "assistant"; content: string };

export type HermesChatResponse = {
  schemaVersion: "tradvera.hermes-chat-response.v1";
  content: string;
  abstained: boolean;
  provider: string;
  traceId: string;
  delegation?: {
    commandId: string;
    targetAgentId: "tradvera.hermes.opportunity-center.v1" | "tradvera.hermes.company-evaluation.v1" | "tradvera.hermes.decision-room.v1";
    status: "COMPLETED";
    sourceId: string;
    sourceHashSha256: string;
  };
};

const endpoint = (import.meta.env.VITE_COMPANION_ENDPOINT as string | undefined)?.replace(/\/$/, "")
  ?? "https://api.tradvera.com/v1/companion";
const adminEndpoint = endpoint.replace(/\/v1\/companion$/, "/v1/admin");
const tradeEvidenceEndpoint = (import.meta.env.VITE_TRADE_EVIDENCE_ENDPOINT as string | undefined)?.replace(/\/$/, "")
  ?? "https://api.tradvera.com/v1/trade/evidence";

export async function companionRequest<T>(user: User, path: string, init?: RequestInit): Promise<T> {
  const token = await user.getIdToken();
  const response = await fetch(`${endpoint}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body?.error === "string" ? body.error : `COMPANION_HTTP_${response.status}`);
  return body as T;
}

export async function bootstrapOwnerPush(user: User) {
  const token = await user.getIdToken();
  const response = await fetch(`${adminEndpoint}/webpush/bootstrap-owner`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: "{}",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body?.error === "string" ? body.error : `WEBPUSH_BOOTSTRAP_HTTP_${response.status}`);
  return body as { created: boolean; integration: { config?: { vapidPublic?: string } } };
}

export async function hermesChatRequest(user: User, input: {
  sessionId: string;
  locale: "ar" | "en";
  message: string;
  history: HermesChatMessage[];
  decisionRoom?: OpportunityDecisionRoom | null;
}) {
  const token = await user.getIdToken();
  const response = await fetch("https://api.tradvera.com/trade/hermes-chat", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body?.error === "string" ? body.error : `HERMES_HTTP_${response.status}`);
  return body as HermesChatResponse;
}

export async function decisionRoomRequest<T>(user: User, path = "", init?: RequestInit): Promise<T> {
  const token = await user.getIdToken();
  const response = await fetch(`${tradeEvidenceEndpoint}/oai/decision-room${path}`, {
    ...init,
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json", ...(init?.headers ?? {}) },
    cache: "no-store",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof body?.error === "string" ? body.error : `DECISION_ROOM_HTTP_${response.status}`);
  return body as T;
}

export function pushKeyBytes(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}
